/**
 * 🏢 Corporate Account Selector Component
 * Reusable component for selecting corporate accounts in booking forms
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Business,
  AccountBalance,
  Warning,
} from '@mui/icons-material';
import {
  corporateAccountsService,
  CorporateAccount,
} from '@/services/corporateAccountsService';

interface CorporateAccountSelectorProps {
  value: string | null;
  onChange: (accountId: string | null, account: CorporateAccount | null) => void;
  disabled?: boolean;
  error?: string;
  onCreditValidation?: (hasCredit: boolean, availableCredit: number) => void;
  bookingAmount?: number; // Amount to validate against credit
}

const formatCurrency = (amount: number | undefined | null, currency: string = 'NGN') => {
  if (amount === undefined || amount === null || isNaN(amount)) {
    // Return appropriate zero based on currency
    return currency === 'NGN' ? '₦0.00' : '$0.00';
  }

  // Use appropriate locale based on currency
  const locale = currency === 'NGN' ? 'en-NG' : 'en-US';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

export const CorporateAccountSelector: React.FC<CorporateAccountSelectorProps> = ({
  value,
  onChange,
  disabled,
  error,
  onCreditValidation,
  bookingAmount,
}) => {
  const [accounts, setAccounts] = useState<CorporateAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<CorporateAccount | null>(null);

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    // Update selected account when value changes
    if (value) {
      const account = accounts.find(acc => acc.id === value);
      setSelectedAccount(account || null);

      // Validate credit if booking amount is provided
      if (account && bookingAmount !== undefined && onCreditValidation) {
        const hasCredit = account.availableCredit >= bookingAmount;
        onCreditValidation(hasCredit, account.availableCredit);
      }
    } else {
      setSelectedAccount(null);
      if (onCreditValidation) {
        onCreditValidation(true, 0); // Reset validation when no account selected
      }
    }
  }, [value, accounts, bookingAmount]);

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const response = await corporateAccountsService.getCorporateAccounts({
        status: 'active', // Only load active accounts
        limit: 100, // Load all active accounts
      });

      if (response.success && response.data) {
        setAccounts(response.data.accounts);
      }
    } catch (error) {
      console.error('Failed to load corporate accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const accountId = event.target.value || null;
    const account = accounts.find(acc => acc.id === accountId) || null;
    onChange(accountId, account);
  };

  const getCreditWarningLevel = (account: CorporateAccount, amount: number): 'error' | 'warning' | null => {
    if (account.availableCredit < amount) {
      return 'error';
    }
    if (account.availableCredit < amount * 1.1) { // Less than 10% buffer
      return 'warning';
    }
    return null;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading corporate accounts...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TextField
        fullWidth
        select
        label="Corporate Account (Optional)"
        value={value || ''}
        onChange={handleChange}
        disabled={disabled || accounts.length === 0}
        error={!!error}
        helperText={error || (accounts.length === 0 ? 'No active corporate accounts available' : 'Select a corporate account for corporate bookings')}
        InputProps={{
          startAdornment: value ? <Business sx={{ mr: 1, color: 'primary.main' }} /> : null,
        }}
      >
        <MenuItem value="">
          <em>None (Regular Customer Booking)</em>
        </MenuItem>
        {accounts.map((account) => (
          <MenuItem key={account.id} value={account.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Box>
                <Typography variant="body2">{account.companyName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Available: {formatCurrency(account.availableCredit, account.currency)}
                </Typography>
              </Box>
              {bookingAmount && account.availableCredit < bookingAmount && (
                <Chip
                  label="Insufficient"
                  size="small"
                  color="error"
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
          </MenuItem>
        ))}
      </TextField>

      {/* Credit Information for Selected Account */}
      {selectedAccount && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AccountBalance fontSize="small" color="primary" />
            <Typography variant="subtitle2">Credit Information</Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Credit Limit:</Typography>
            <Typography variant="body2" fontWeight="medium">
              {formatCurrency(selectedAccount.creditLimit, selectedAccount.currency)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Current Balance:</Typography>
            <Typography variant="body2" color="error.main">
              {formatCurrency(selectedAccount.currentBalance, selectedAccount.currency)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Available Credit:</Typography>
            <Typography variant="body2" fontWeight="medium" color="success.main">
              {formatCurrency(selectedAccount.availableCredit, selectedAccount.currency)}
            </Typography>
          </Box>

          {/* Booking Amount Validation */}
          {bookingAmount !== undefined && bookingAmount > 0 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">Booking Amount:</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatCurrency(bookingAmount, selectedAccount.currency)}
                </Typography>
              </Box>

              {getCreditWarningLevel(selectedAccount, bookingAmount) === 'error' && (
                <Alert severity="error" icon={<Warning />} sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Insufficient Credit:</strong> This booking exceeds the available credit limit.
                    Available: {formatCurrency(selectedAccount.availableCredit, selectedAccount.currency)}
                  </Typography>
                </Alert>
              )}

              {getCreditWarningLevel(selectedAccount, bookingAmount) === 'warning' && (
                <Alert severity="warning" icon={<Warning />} sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Low Credit Warning:</strong> This booking will leave minimal available credit.
                    Remaining: {formatCurrency(selectedAccount.availableCredit - bookingAmount, selectedAccount.currency)}
                  </Typography>
                </Alert>
              )}

              {getCreditWarningLevel(selectedAccount, bookingAmount) === null && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    Credit sufficient. Remaining after booking: {formatCurrency(selectedAccount.availableCredit - bookingAmount, selectedAccount.currency)}
                  </Typography>
                </Alert>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};
